// src/components/JobView/Tabs/FilesAndDocsDisplayTab.js
//
// Gestor de archivos del trabajo. Permite subir PDF, fotos, archivos de diseño,
// originales, fuentes o bases de datos, describiendo QUÉ es y PARA QUÉ sirve cada uno.
// La subida real (y su barra de progreso en vivo) la maneja JobView a través de los
// props `uploads` y `onUpload`, de modo que la carga continúa en segundo plano aunque
// se cambie de pestaña.

import React, { useMemo, useRef, useState } from 'react';
import {
  UploadCloud, FileText, Image as ImageIcon, Palette, FileImage, Type, Database,
  File as FileIcon, Eye, Trash2, Pencil, X, Loader2, Search, Check, AlertTriangle
} from 'lucide-react';

// --- Categorías de archivo (qué tipo de material es) ---
const FILE_CATEGORIES = [
  { value: 'pdf',      label: 'PDF / Documento',     icon: FileText },
  { value: 'imagen',   label: 'Foto / Imagen',       icon: ImageIcon },
  { value: 'diseno',   label: 'Archivo de diseño',   icon: Palette },
  { value: 'original', label: 'Original / Arte',     icon: FileImage },
  { value: 'fuente',   label: 'Fuente tipográfica',  icon: Type },
  { value: 'datos',    label: 'Base de datos',       icon: Database },
  { value: 'otro',     label: 'Otro',                icon: FileIcon },
];

const CATEGORY_MAP = FILE_CATEGORIES.reduce((acc, c) => { acc[c.value] = c; return acc; }, {});

// Adivina la categoría a partir del tipo MIME / extensión, para preseleccionarla.
const guessCategory = (file) => {
  const name = (file.name || '').toLowerCase();
  const type = (file.type || '').toLowerCase();
  if (type.startsWith('image/')) return 'imagen';
  if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  if (/\.(ai|psd|indd|eps|svg|cdr|sketch|fig|xd|afdesign|afphoto)$/.test(name)) return 'diseno';
  if (/\.(ttf|otf|woff2?|eot)$/.test(name)) return 'fuente';
  if (/\.(csv|xlsx?|json|sql|db|sqlite|accdb|mdb|xml)$/.test(name)) return 'datos';
  if (/\.(tiff?|raw|cr2|nef|dng|arw)$/.test(name)) return 'original';
  return 'otro';
};

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let val = bytes / 1024;
  let i = 0;
  while (val >= 1024 && i < units.length - 1) { val /= 1024; i++; }
  return `${val.toFixed(val < 10 ? 1 : 0)} ${units[i]}`;
};

const formatDate = (ms) => {
  if (!ms) return '';
  try {
    return new Date(ms).toLocaleString('es-ES', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return ''; }
};

const CategoryIcon = ({ category, size = 18, className = '' }) => {
  const Icon = (CATEGORY_MAP[category] || CATEGORY_MAP.otro).icon;
  return <Icon size={size} className={className} />;
};

const FilesAndDocsDisplayTab = ({
  jobData,
  uploads = [],          // subidas en curso/recientes para este trabajo (las gestiona JobView)
  onUpload = () => {},   // (file, { description, category }) => void
  onDeleteDocument = () => {},     // (index) => void
  onUpdateDocument = () => {},     // (index, { description, category }) => void
}) => {
  const [staged, setStaged] = useState([]); // [{ key, file, description, category }]
  const [isDragging, setIsDragging] = useState(false);
  const [filter, setFilter] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editDraft, setEditDraft] = useState({ description: '', category: 'otro' });
  const keyRef = useRef(0);
  const fileInputRef = useRef(null);

  const documents = useMemo(() => (jobData?.documents || []).filter(Boolean), [jobData]);

  const filteredDocs = useMemo(() => {
    const q = filter.trim().toLowerCase();
    // Mantenemos el índice original para borrar/editar de forma fiable.
    const withIndex = documents.map((doc, index) => ({ doc, index }));
    if (!q) return withIndex;
    return withIndex.filter(({ doc }) =>
      `${doc.name || ''} ${doc.description || ''} ${CATEGORY_MAP[doc.category]?.label || ''}`
        .toLowerCase().includes(q)
    );
  }, [documents, filter]);

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setStaged(prev => [
      ...prev,
      ...files.map(file => ({
        key: ++keyRef.current,
        file,
        description: '',
        category: guessCategory(file),
      })),
    ]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const updateStaged = (key, patch) => {
    setStaged(prev => prev.map(item => item.key === key ? { ...item, ...patch } : item));
  };

  const removeStaged = (key) => {
    setStaged(prev => prev.filter(item => item.key !== key));
  };

  const uploadStaged = (item) => {
    onUpload(item.file, { description: item.description.trim(), category: item.category });
    removeStaged(item.key);
  };

  const uploadAll = () => {
    staged.forEach(item => onUpload(item.file, { description: item.description.trim(), category: item.category }));
    setStaged([]);
  };

  const startEditing = (index, doc) => {
    setEditingIndex(index);
    setEditDraft({ description: doc.description || '', category: doc.category || 'otro' });
  };

  const saveEditing = (index) => {
    onUpdateDocument(index, { description: editDraft.description.trim(), category: editDraft.category });
    setEditingIndex(null);
  };

  const activeUploads = uploads.filter(u => u.status === 'uploading' || u.status === 'error');

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b pb-3">
        <div>
          <h3 className="text-lg font-bold text-gray-800">Archivos y Documentos del Trabajo</h3>
          <p className="text-sm text-gray-500">
            Sube PDF, fotos, archivos de diseño, originales, fuentes o bases de datos. Describe qué es y para qué sirve cada uno.
          </p>
        </div>
        {documents.length > 0 && (
          <div className="relative w-full sm:w-64">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Buscar archivo..."
              className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
            />
          </div>
        )}
      </div>

      {/* --- Zona de carga (drag & drop + selección) --- */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`flex flex-col items-center justify-center px-4 py-8 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
          isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
        }`}
      >
        <UploadCloud size={36} className={isDragging ? 'text-indigo-500' : 'text-gray-400'} />
        <p className="mt-2 text-sm font-semibold text-gray-700">
          Arrastra archivos aquí o haz clic para seleccionar
        </p>
        <p className="text-xs text-gray-400">Puedes seleccionar varios a la vez</p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
        />
      </div>

      {/* --- Archivos en preparación (pendientes de subir) --- */}
      {staged.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-bold text-gray-700">
              Listos para subir ({staged.length})
            </h4>
            <div className="flex gap-2">
              <button
                onClick={() => setStaged([])}
                className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancelar todo
              </button>
              <button
                onClick={uploadAll}
                className="px-3 py-1.5 bg-indigo-600 text-white text-sm font-semibold rounded-lg shadow-sm hover:bg-indigo-700 flex items-center gap-1.5"
              >
                <UploadCloud size={16} /> Subir todos
              </button>
            </div>
          </div>
          {staged.map((item) => (
            <div key={item.key} className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <CategoryIcon category={item.category} size={22} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-grow min-w-0">
                  <p className="font-semibold text-gray-800 truncate">{item.file.name}</p>
                  <p className="text-xs text-gray-500">{formatBytes(item.file.size)}</p>
                </div>
                <button onClick={() => removeStaged(item.key)} className="p-1 text-gray-400 hover:text-red-600" title="Quitar">
                  <X size={18} />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[200px,1fr] gap-3">
                <select
                  value={item.category}
                  onChange={(e) => updateStaged(item.key, { category: e.target.value })}
                  className="text-sm border rounded-lg px-2 py-2 bg-white focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                >
                  {FILE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <input
                  type="text"
                  value={item.description}
                  onChange={(e) => updateStaged(item.key, { description: e.target.value })}
                  placeholder="¿Qué es y para qué sirve? (ej. Arte final aprobado por cliente)"
                  className="text-sm border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => uploadStaged(item)}
                  className="px-3 py-1.5 bg-emerald-600 text-white text-sm font-semibold rounded-lg shadow-sm hover:bg-emerald-700 flex items-center gap-1.5"
                >
                  <UploadCloud size={16} /> Subir
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* --- Subidas en curso (barra de progreso en vivo) --- */}
      {activeUploads.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-bold text-gray-700">Subiendo…</h4>
          {activeUploads.map((u) => (
            <div key={u.id} className="p-3 bg-white border rounded-lg">
              <div className="flex items-center gap-2 mb-1.5">
                {u.status === 'error'
                  ? <AlertTriangle size={16} className="text-red-500 flex-shrink-0" />
                  : <Loader2 size={16} className="text-indigo-500 animate-spin flex-shrink-0" />}
                <span className="text-sm font-medium text-gray-700 truncate flex-grow">{u.name}</span>
                <span className={`text-xs font-semibold ${u.status === 'error' ? 'text-red-500' : 'text-indigo-600'}`}>
                  {u.status === 'error' ? 'Error' : `${Math.round(u.progress)}%`}
                </span>
              </div>
              {u.status === 'error' ? (
                <p className="text-xs text-red-500">{u.error || 'No se pudo subir el archivo.'}</p>
              ) : (
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div className="bg-indigo-600 h-2 rounded-full transition-all duration-200" style={{ width: `${u.progress}%` }} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* --- Documentos ya guardados --- */}
      <div>
        <h4 className="text-sm font-bold text-gray-700 mb-3">
          Archivos guardados ({documents.length})
        </h4>
        {documents.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed">
            <FileText className="mx-auto text-gray-300" size={40} />
            <p className="text-gray-500 mt-2">Aún no hay archivos en este trabajo.</p>
            <p className="text-sm text-gray-400">Arrastra o selecciona archivos arriba para empezar.</p>
          </div>
        ) : filteredDocs.length === 0 ? (
          <p className="text-sm text-gray-400 py-6 text-center">Ningún archivo coincide con «{filter}».</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {filteredDocs.map(({ doc, index }) => {
              const cat = CATEGORY_MAP[doc.category] || CATEGORY_MAP.otro;
              const isEditing = editingIndex === index;
              return (
                <div key={doc.id || index} className="p-4 bg-white border rounded-lg hover:shadow-sm transition-shadow flex gap-3">
                  <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-indigo-50 flex items-center justify-center">
                    <CategoryIcon category={doc.category} size={22} className="text-indigo-600" />
                  </div>
                  <div className="flex-grow min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        {doc.url ? (
                          <a href={doc.url} target="_blank" rel="noopener noreferrer"
                             className="font-semibold text-gray-800 hover:text-indigo-600 hover:underline truncate block">
                            {doc.name || 'Archivo sin nombre'}
                          </a>
                        ) : (
                          <p className="font-semibold text-gray-500 truncate">{doc.name || 'Archivo sin nombre'}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-400 mt-0.5">
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-medium">
                            <cat.icon size={11} /> {cat.label}
                          </span>
                          {doc.size ? <span>{formatBytes(doc.size)}</span> : null}
                          {doc.uploadedAt ? <span>· {formatDate(doc.uploadedAt)}</span> : null}
                          {doc.uploadedByName ? <span>· {doc.uploadedByName}</span> : null}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {doc.url && (
                          <a href={doc.url} target="_blank" rel="noopener noreferrer"
                             className="p-1.5 text-gray-400 hover:text-indigo-600" title="Ver / Descargar">
                            <Eye size={17} />
                          </a>
                        )}
                        <button onClick={() => startEditing(index, doc)} className="p-1.5 text-gray-400 hover:text-indigo-600" title="Editar descripción">
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => { if (window.confirm(`¿Eliminar «${doc.name || 'este archivo'}»? No se puede deshacer.`)) onDeleteDocument(index); }}
                          className="p-1.5 text-gray-400 hover:text-red-600" title="Eliminar">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    {isEditing ? (
                      <div className="mt-2 space-y-2">
                        <select
                          value={editDraft.category}
                          onChange={(e) => setEditDraft(d => ({ ...d, category: e.target.value }))}
                          className="w-full text-sm border rounded px-2 py-1.5 bg-white"
                        >
                          {FILE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                        <textarea
                          value={editDraft.description}
                          onChange={(e) => setEditDraft(d => ({ ...d, description: e.target.value }))}
                          rows={2}
                          placeholder="¿Qué es y para qué sirve?"
                          className="w-full text-sm border rounded px-2 py-1.5 focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                        />
                        <div className="flex justify-end gap-2">
                          <button onClick={() => setEditingIndex(null)} className="px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded">Cancelar</button>
                          <button onClick={() => saveEditing(index)} className="px-2.5 py-1 text-xs bg-indigo-600 text-white rounded flex items-center gap-1 hover:bg-indigo-700">
                            <Check size={13} /> Guardar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600 mt-1 break-words">
                        {doc.description || <span className="text-gray-300 italic">Sin descripción</span>}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilesAndDocsDisplayTab;
